import * as rq from '@tanstack/react-query';

import { appConfig } from '@/config';
import type { DeviceBase } from '@/entities/device';
import { useAuth } from '@/hooks/use-auth';
import { useMe } from '@/hooks/use-me';

import { commonV1DeviceLineageList } from '../../__generated__/oazapfts-client';
import { mapDeviceBase } from '../../mappers/entities/device.mapper';

/**
 * Query namespace for fetching device lists from lineage data.
 *
 * This query retrieves a hierarchical list of devices based on a source device ID
 * and the desired hierarchy level. Searches ancestors and children only.
 */
export namespace GetDeviceListFromLineage {
  export const key = (input: Input) => [
    'devices',
    'get-device-list-from-lineage',
    ...Object.entries(input).flatMap(([key, value]) => [key, value]),
  ];

  export type Params = { refetchInterval?: number };
  export type Input = {
    /** The referenced device ID to fetch lineage from */
    id: string | undefined;
    /** The hierarchy level to retrieve devices for */
    level:
      | 'array'
      | 'bank'
      | 'cell'
      | 'container'
      | 'module'
      | 'node'
      | 'pack'
      | 'system'
      | undefined;
  };

  export type Output = DeviceBase[] | null;

  export function useQueryFn() {
    const auth = useAuth();
    const me = useMe();

    return async function query(input: Input): Promise<Output> {
      if (!input.id || !input.level) {
        return null;
      }

      const accessToken = await auth.getAccessToken();
      const result = await commonV1DeviceLineageList(
        {
          accureId: input.id,
          level: input.level,
          xUserName: me.displayName,
          xUserEmail: me.email,
        },
        {
          baseUrl: `${appConfig.bffUrl}/plex`,
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        },
      );

      if (result.status === 200) {
        const deviceList = result.data;
        if (!deviceList) {
          return null;
        }

        return deviceList.map(mapDeviceBase);
      }

      return null;
    };
  }

  export function useQuery(input: Input, { refetchInterval }: Params = {}) {
    const queryFn = useQueryFn();

    return rq.useQuery<Output>({
      refetchInterval,
      queryKey: key(input),
      enabled: Boolean(input.id) && Boolean(input.level),
      queryFn: async () => queryFn(input),
    });
  }

  export function usePrefetch() {
    const queryClient = rq.useQueryClient();
    const queryFn = useQueryFn();

    return async function prefetch(input: Input): Promise<void> {
      await queryClient.prefetchQuery<Output>({
        queryKey: key(input),
        queryFn: () => queryFn(input),
      });
    };
  }
}
